document.addEventListener('DOMContentLoaded', () => {
  initMysteryGiftHandler();
  document.addEventListener('theme:cart:refresh', initMysteryGiftHandler);
  document.addEventListener('theme:product:added', initMysteryGiftHandler);
  document.addEventListener('theme:cart-drawer:open', initMysteryGiftHandler);
});

function initMysteryGiftHandler() {
  // Only initialize if mystery gift is enabled
  if (!window.theme?.mysteryGift?.enabled) return;
  
  // Debug logging
  if (window.theme?.mysteryGift?.debug) {
    console.log('[Mystery Gift Handler] Initializing...');
  }

  // Get all cart remove buttons for regular items (not mystery gifts)
  const removeButtons = document.querySelectorAll('[data-item-remove]');
  
  removeButtons.forEach(button => {
    // Skip if already has our listener
    if (button.dataset.mysteryGiftListener === 'true') return;
    
    // Mark as having our listener
    button.dataset.mysteryGiftListener = 'true';
    
    // Add our click handler BEFORE the default one
    button.addEventListener('click', handleRemoveClick, true);
  });
  
  if (window.theme?.mysteryGift?.debug) {
    console.log('[Mystery Gift Handler] Added listeners to', removeButtons.length, 'remove buttons');
  }
}

async function handleRemoveClick(event) {
  const removeButton = event.currentTarget;
  const cartItem = removeButton.closest('[data-item]');
  
  if (!cartItem) return;
  
  // Check if this is a mystery gift item - if so, don't interfere
  const isMysteryGift = checkIfMysteryGift(cartItem);
  if (isMysteryGift) return;
  
  // Check if button is already disabled (theme's loading state)
  if (removeButton.hasAttribute('disabled') || removeButton.classList.contains('is-disabled')) {
    return;
  }
  
  try {
    // Get current cart state BEFORE any removal
    const cartResponse = await fetch('/cart.js');
    const cart = await cartResponse.json();
    
    // Get the item key being removed
    const itemKey = removeButton.dataset.id;
    const itemBeingRemoved = cart.items.find(item => item.key === itemKey);
    
    if (!itemBeingRemoved) return;
    
    // Check if this will be the last non-mystery gift item OR if we need to update mystery gifts
    const willBeLastMainItem = isLastMainItem(cart.items, itemKey);
    const shouldUpdateMysteryGifts = shouldInterceptForMysteryGiftUpdate(cart.items, itemKey);
    
    if (window.theme?.mysteryGift?.debug) {
      console.log('[Mystery Gift Handler] Will be last main item:', willBeLastMainItem);
      console.log('[Mystery Gift Handler] Should update mystery gifts:', shouldUpdateMysteryGifts);
      console.log('[Mystery Gift Handler] Current cart items:', cart.items.length);
      console.log('[Mystery Gift Handler] Cart items details:', cart.items);
    }
    
    if (willBeLastMainItem || shouldUpdateMysteryGifts) {
      if (willBeLastMainItem) {
        console.log('Removing last main item - will also remove mystery gifts');
      } else {
        console.log('Removing main item - will update mystery gift quantities');
      }
      
      // Prevent the default remove action completely
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      
      // Handle the removal with mystery gift cleanup/update
      await removeMainItemWithMysteryGiftUpdate(itemKey, cartItem, removeButton, cart);
    }
    
  } catch (error) {
    console.error('Error in mystery gift handler:', error);
    // Let default behavior continue on error
  }
}

function checkIfMysteryGift(cartItem) {
  // Method 1: Check using theme's mystery gift configuration
  if (window.theme?.mysteryGift?.products) {
    const itemTitle = cartItem.querySelector('[data-item-title]')?.textContent || '';
    const productTitle = cartItem.querySelector('.cart__item-title')?.textContent || '';
    const title = (itemTitle + ' ' + productTitle).toLowerCase();
    
    // Check if this matches any configured mystery gift product
    const isMysteryProduct = window.theme.mysteryGift.products.some(mgProduct => {
      return title.includes(mgProduct.handle) || title.includes(mgProduct.title.toLowerCase());
    });
    
    if (isMysteryProduct) return true;
  }
  
  // Method 2: Check for mystery gift wrapper class or data attribute
  if (cartItem.classList.contains('cart__item--mystery') || 
      cartItem.hasAttribute('data-mystery-gift') ||
      cartItem.querySelector('.cart__quantity-wrapper--mystery')) {
    return true;
  }
  
  // Method 3: Check item title for mystery gift indicators
  const itemTitle = cartItem.querySelector('[data-item-title]')?.textContent || '';
  const productTitle = cartItem.querySelector('.cart__item-title')?.textContent || '';
  const title = (itemTitle + ' ' + productTitle).toLowerCase();
  
  // Add your mystery gift product name patterns here
  const mysteryGiftPatterns = [
    'mystery',
    'gift',
    'bonus',
    'free',
    'complimentary'
  ];
  
  if (mysteryGiftPatterns.some(pattern => title.includes(pattern))) {
    return true;
  }
  
  // Method 4: Check if quantity display is read-only (mystery gifts typically have read-only quantity)
  const quantityDisplay = cartItem.querySelector('.cart__quantity-display');
  if (quantityDisplay && !cartItem.querySelector('.cart__quantity-field')) {
    return true;
  }
  
  return false;
}

function shouldInterceptForMysteryGiftUpdate(cartItems, itemKeyToRemove) {
  // Check if there are any mystery gifts in cart
  const hasMysteryGifts = cartItems.some(item => {
    let isMystery = 
      (item.properties && item.properties._is_mystery_gift) ||
      (item.properties && item.properties.mystery_gift) ||
      (item.vendor && item.vendor.toLowerCase().includes('mystery')) ||
      (item.product_type && item.product_type.toLowerCase().includes('mystery')) ||
      (item.title && item.title.toLowerCase().includes('mystery')) ||
      (item.product_title && item.product_title.toLowerCase().includes('mystery'));
    
    // Also check using theme's mystery gift configuration
    if (!isMystery && window.theme?.mysteryGift?.products) {
      isMystery = window.theme.mysteryGift.products.some(mgProduct => {
        return mgProduct.id === item.product_id || 
               mgProduct.variantId === item.variant_id ||
               mgProduct.handle === item.handle;
      });
    }
    
    return isMystery;
  });
  
  if (!hasMysteryGifts) return false;
  
  // Check if the item being removed is an upgraded item
  const itemToRemove = cartItems.find(item => item.key === itemKeyToRemove);
  const isRemovingUpgradedItem = itemToRemove && 
    itemToRemove.properties && 
    itemToRemove.properties._is_upsell_item;
  
  if (window.theme?.mysteryGift?.debug) {
    console.log('[Mystery Gift Handler] Has mystery gifts:', hasMysteryGifts);
    console.log('[Mystery Gift Handler] Is removing upgraded item:', isRemovingUpgradedItem);
  }
  
  return hasMysteryGifts && isRemovingUpgradedItem;
}

function isLastMainItem(cartItems, itemKeyToRemove) {
  // Count non-mystery gift items excluding the one being removed
  let mainItemCount = 0;
  let itemToRemoveIsMystery = false;
  
  cartItems.forEach(item => {
    // Check multiple ways to identify mystery gifts
    let isMystery = 
      (item.properties && item.properties._is_mystery_gift) ||
      (item.properties && item.properties.mystery_gift) ||
      (item.vendor && item.vendor.toLowerCase().includes('mystery')) ||
      (item.product_type && item.product_type.toLowerCase().includes('mystery')) ||
      (item.title && item.title.toLowerCase().includes('mystery')) ||
      (item.product_title && item.product_title.toLowerCase().includes('mystery'));
    
    // Also check using theme's mystery gift configuration
    if (!isMystery && window.theme?.mysteryGift?.products) {
      isMystery = window.theme.mysteryGift.products.some(mgProduct => {
        return mgProduct.id === item.product_id || 
               mgProduct.variantId === item.variant_id ||
               mgProduct.handle === item.handle;
      });
    }
    
    if (window.theme?.mysteryGift?.debug) {
      console.log('[Mystery Gift Handler] Item analysis:', {
        key: item.key,
        title: item.title || item.product_title,
        isMystery: isMystery,
        properties: item.properties,
        vendor: item.vendor,
        product_type: item.product_type
      });
    }
    
    if (item.key === itemKeyToRemove) {
      itemToRemoveIsMystery = isMystery;
      return; // Skip counting this item since it's being removed
    }
    
    if (!isMystery) {
      mainItemCount++;
    }
  });
  
  // If we're removing a mystery gift, don't interfere
  if (itemToRemoveIsMystery) {
    if (window.theme?.mysteryGift?.debug) {
      console.log('[Mystery Gift Handler] Removing mystery gift item, not interfering');
    }
    return false;
  }
  
  // Return true if this will be the last main item (no other main items remaining)
  const isLastMain = mainItemCount === 0;
  
  if (window.theme?.mysteryGift?.debug) {
    console.log('[Mystery Gift Handler] Main items remaining after removal:', mainItemCount);
    console.log('[Mystery Gift Handler] Is last main item:', isLastMain);
  }
  
  return isLastMain;
}

async function removeMainItemWithMysteryGiftUpdate(itemKey, cartItemElement, removeButton, currentCart) {
  // Show loading state using theme's classes
  cartItemElement.classList.add('is-loading');
  removeButton.disabled = true;
  removeButton.classList.add('is-disabled');
  
  // Also add loading to cart if available
  const cart = document.querySelector('[data-cart-page], cart-drawer');
  if (cart) {
    cart.classList.add('is-loading');
  }
  
  try {
    // Use the cart state we already have
    const cart = currentCart;
    
    // Get all mystery gift items that should be removed
    const mysteryGiftItems = cart.items.filter(item => {
      // Check multiple ways to identify mystery gifts
      let isMystery = 
        (item.properties && item.properties._is_mystery_gift) ||
        (item.properties && item.properties.mystery_gift) ||
        (item.vendor && item.vendor.toLowerCase().includes('mystery')) ||
        (item.product_type && item.product_type.toLowerCase().includes('mystery')) ||
        (item.title && item.title.toLowerCase().includes('mystery')) ||
        (item.product_title && item.product_title.toLowerCase().includes('mystery'));
      
      // Also check using theme's mystery gift configuration
      if (!isMystery && window.theme?.mysteryGift?.products) {
        isMystery = window.theme.mysteryGift.products.some(mgProduct => {
          return mgProduct.id === item.product_id || 
                 mgProduct.variantId === item.variant_id ||
                 mgProduct.handle === item.handle;
        });
      }
      
      return isMystery;
    });
    
    console.log('Found mystery gift items to remove:', mysteryGiftItems.length);
    if (window.theme?.mysteryGift?.debug) {
      console.log('[Mystery Gift Handler] Mystery gift items:', mysteryGiftItems);
    }
    
    // Find item index for the main item
    const itemIndex = cart.items.findIndex(item => item.key === itemKey);
    if (itemIndex === -1) {
      throw new Error('Item not found in cart');
    }
    
    // Strategy: Use same approach as cart toggle - clear and rebuild entire cart
    console.log('Using cart clear and rebuild strategy to update mystery gifts');
    
    // Create the final, ordered list of items EXCLUDING the item being removed
    let finalItemsToAdd = cart.items
      .filter(item => item.key !== itemKey) // Remove the target item
      .map(item => {
        // Skip mystery gift items - they will be handled below  
        // Use comprehensive mystery gift detection
        let isMystery = 
          (item.properties && item.properties._is_mystery_gift) ||
          (item.properties && item.properties.mystery_gift) ||
          (item.vendor && item.vendor.toLowerCase().includes('mystery')) ||
          (item.product_type && item.product_type.toLowerCase().includes('mystery')) ||
          (item.title && item.title.toLowerCase().includes('mystery')) ||
          (item.product_title && item.product_title.toLowerCase().includes('mystery'));
        
        // Also check using theme's mystery gift configuration
        if (!isMystery && window.theme?.mysteryGift?.products) {
          isMystery = window.theme.mysteryGift.products.some(mgProduct => {
            return mgProduct.id === item.product_id || 
                   mgProduct.variantId === item.variant_id ||
                   mgProduct.handle === item.handle;
          });
        }
        
        if (isMystery) {
          return null;
        }
        
        // Return non-mystery items in a format suitable for the add.js API
        return {
          id: item.variant_id,
          quantity: item.quantity,
          properties: item.properties || {},
        };
      })
      .filter(item => item !== null); // Remove null entries (removed mystery gifts)

    // Calculate how many items will be upgraded after removal
    const upgradedCount = finalItemsToAdd.filter(item => 
      item.properties && item.properties._is_upsell_item
    ).length;

    console.log('Upgraded items remaining after removal:', upgradedCount);

    // Add mystery gifts based on upgraded count (same logic as cart toggle)
    if (window.theme?.mysteryGift?.enabled && window.theme?.mysteryGift?.products?.length > 0) {
      if (upgradedCount > 0) {
        // Add all selected mystery gift products, each with quantity equal to upgraded count
        window.theme.mysteryGift.products.forEach((product, index) => {
          const mysteryGiftItem = {
            id: product.variantId,
            quantity: upgradedCount, // Quantity equals number of upgraded items
            properties: {
              '_is_mystery_gift': true,
              '_mystery_gift_for': 'multiple',
              '_mystery_gift_index': index,
            },
          };
          finalItemsToAdd.push(mysteryGiftItem);
        });
        
        console.log('Mystery gifts will be re-added:', window.theme.mysteryGift.products.length, 'products, each with quantity:', upgradedCount);
      } else {
        console.log('No mystery gifts to re-add - no upgraded items remaining');
      }
    }
    
    console.log('Final items to rebuild cart with:', finalItemsToAdd.length);

    // Clear the entire cart first
    await fetch('/cart/clear.js', { method: 'POST' });

    // Re-add all items in REVERSE ORDER to counteract the API's prepending behavior (same as cart toggle)
    if (finalItemsToAdd.length > 0) {
      for (let i = finalItemsToAdd.length - 1; i >= 0; i--) {
        const item = finalItemsToAdd[i];
        await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(item),
        });
      }
      
      console.log('Successfully rebuilt cart with', finalItemsToAdd.length, 'items in correct order');
    }
    
    // Dispatch event to refresh cart UI (this is what theme expects)
    document.dispatchEvent(new CustomEvent('theme:cart:refresh'));
    
    // Also dispatch the product added event to trigger any other cart updates
    document.dispatchEvent(new CustomEvent('theme:product:added', {
      bubbles: true,
    }));
    
    console.log('Successfully updated cart with mystery gifts');
    
  } catch (error) {
    console.error('Error removing items:', error);
    
    // Remove loading states on error
    cartItemElement.classList.remove('is-loading');
    removeButton.disabled = false;
    removeButton.classList.remove('is-disabled');
    
    const cart = document.querySelector('[data-cart-page], cart-drawer');
    if (cart) {
      cart.classList.remove('is-loading');
    }
    
    // Show user-friendly error if needed
    console.warn('Failed to remove mystery gifts automatically. Please refresh the page.');
  }
}

// Utility function to manually clean orphaned mystery gifts
window.cleanOrphanedMysteryGifts = async function() {
  if (!window.theme?.mysteryGift?.enabled) {
    console.log('Mystery gift not enabled');
    return;
  }
  
  try {
    const cartResponse = await fetch('/cart.js');
    const cart = await cartResponse.json();
    
    const mainItems = cart.items.filter(item => {
      // Check multiple ways to identify mystery gifts
      let isMystery = 
        (item.properties && item.properties._is_mystery_gift) ||
        (item.properties && item.properties.mystery_gift) ||
        (item.vendor && item.vendor.toLowerCase().includes('mystery')) ||
        (item.product_type && item.product_type.toLowerCase().includes('mystery')) ||
        (item.title && item.title.toLowerCase().includes('mystery')) ||
        (item.product_title && item.product_title.toLowerCase().includes('mystery'));
      
      // Also check using theme's mystery gift configuration
      if (!isMystery && window.theme?.mysteryGift?.products) {
        isMystery = window.theme.mysteryGift.products.some(mgProduct => {
          return mgProduct.id === item.product_id || 
                 mgProduct.variantId === item.variant_id ||
                 mgProduct.handle === item.handle;
        });
      }
      
      return !isMystery;
    });
    
    const mysteryGiftItems = cart.items.filter(item => {
      // Check multiple ways to identify mystery gifts
      let isMystery = 
        (item.properties && item.properties._is_mystery_gift) ||
        (item.properties && item.properties.mystery_gift) ||
        (item.vendor && item.vendor.toLowerCase().includes('mystery')) ||
        (item.product_type && item.product_type.toLowerCase().includes('mystery')) ||
        (item.title && item.title.toLowerCase().includes('mystery')) ||
        (item.product_title && item.product_title.toLowerCase().includes('mystery'));
      
      // Also check using theme's mystery gift configuration
      if (!isMystery && window.theme?.mysteryGift?.products) {
        isMystery = window.theme.mysteryGift.products.some(mgProduct => {
          return mgProduct.id === item.product_id || 
                 mgProduct.variantId === item.variant_id ||
                 mgProduct.handle === item.handle;
        });
      }
      
      return isMystery;
    });
    
    console.log('Main items:', mainItems.length);
    console.log('Mystery gift items:', mysteryGiftItems.length);
    
    if (mainItems.length === 0 && mysteryGiftItems.length > 0) {
      console.log('Found orphaned mystery gifts, removing...');
      
      // Clear entire cart if only mystery gifts remain
      await fetch('/cart/clear.js', { method: 'POST' });
      
      document.dispatchEvent(new CustomEvent('theme:cart:refresh'));
      console.log('Cleaned up orphaned mystery gifts by clearing cart');
    } else {
      console.log('No orphaned mystery gifts found');
    }
    
  } catch (error) {
    console.error('Error cleaning mystery gifts:', error);
  }
};

// Auto-cleanup on page load if there are orphaned mystery gifts
document.addEventListener('DOMContentLoaded', () => {
  // Wait a bit for other scripts to initialize
  setTimeout(() => {
    if (window.theme?.mysteryGift?.enabled) {
      window.cleanOrphanedMysteryGifts();
    }
  }, 1000);
});
